import { useState, useRef, useCallback } from "react"
import type { Script } from "@/types"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Badge } from "@/components/ui/badge"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

const RESIDENTIAL_AUDIENCES = [
  { value: "first-time-buyers", label: "First-time buyers" },
  { value: "growing-families", label: "Growing families" },
  { value: "downsizers", label: "Downsizers" },
  { value: "investors", label: "Investors" },
  { value: "luxury-buyers", label: "Luxury buyers" },
  { value: "young-professionals", label: "Young professionals" },
  { value: "retirees", label: "Retirees" },
  { value: "relocators", label: "Relocators" },
  { value: "developers", label: "Developers" },
] as const

const COMMERCIAL_AUDIENCES = [
  { value: "business-owners", label: "Business owners" },
  { value: "commercial-tenants", label: "Commercial tenants" },
  { value: "franchisees", label: "Franchisees" },
  { value: "office-seekers", label: "Office seekers" },
  { value: "retail-operators", label: "Retail operators" },
] as const

const TONES = [
  { value: "confident", label: "Confident" },
  { value: "luxury", label: "Luxury" },
  { value: "friendly", label: "Friendly" },
  { value: "professional", label: "Professional" },
  { value: "energetic", label: "Energetic" },
  { value: "corporate", label: "Corporate" },
  { value: "informative", label: "Informative" },
] as const

const LANGUAGES = [
  { value: "en", label: "English" },
  { value: "fr", label: "French" },
  { value: "de", label: "German" },
  { value: "el", label: "Greek" },
  { value: "es", label: "Spanish" },
  { value: "it", label: "Italian" },
  { value: "nl", label: "Dutch" },
  { value: "pl", label: "Polish" },
  { value: "pt", label: "Portuguese" },
  { value: "cy", label: "Welsh" },
  { value: "zh", label: "Chinese" },
] as const

type ScriptContent = {
  headline?: string
  scenes?: Array<{ text: string; duration_seconds: number; type?: "closing" }>
  narration?: string
  cta_text?: string
}

type ScriptData = {
  target_audience: string
  tone: string
  language: string
  notes: string
}

export default function WizardStepScript({
  scripts,
  data,
  onChange,
  isGenerating,
  generatingMessage,
  scriptFailed,
  onGenerate,
  onUpdateScript,
}: {
  scripts: Script[]
  data: ScriptData
  onChange: (updates: Partial<ScriptData>) => void
  isGenerating: boolean
  generatingMessage: string
  scriptFailed?: boolean
  onGenerate: (regenerateNotes?: string) => void
  onUpdateScript: (content: Record<string, unknown>) => void
}) {
  const activeScript = scripts.find((s) => s.active)
  const serverContent = activeScript?.content as ScriptContent | undefined
  const [localContent, setLocalContent] = useState<ScriptContent | null>(null)
  const [regenerateNotes, setRegenerateNotes] = useState("")
  const [showRegenerate, setShowRegenerate] = useState(false)
  const [showNotes, setShowNotes] = useState(!!data.notes)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const content = localContent ?? serverContent

  const debouncedSave = useCallback(
    (updated: ScriptContent) => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
      saveTimerRef.current = setTimeout(() => {
        onUpdateScript(updated as Record<string, unknown>)
      }, 800)
    },
    [onUpdateScript],
  )

  function handleGenerate() {
    onGenerate(regenerateNotes || undefined)
    setShowRegenerate(false)
    setRegenerateNotes("")
    setLocalContent(null)
  }

  function updateContent(updates: Partial<ScriptContent>) {
    if (!content) return
    const updated = { ...content, ...updates }
    setLocalContent(updated)
    debouncedSave(updated)
  }

  const contentScenes = content?.scenes?.filter((s) => s.type !== "closing") || []
  const totalDuration = contentScenes.reduce((sum, s) => sum + s.duration_seconds, 0)

  return (
    <div>
      <h2 className="mb-1 font-display text-2xl font-semibold text-charcoal">
        Script
      </h2>
      <p className="mb-6 text-warm-grey">
        Configure your script settings and generate copy for your video.
      </p>

      {/* Settings */}
      <div className="mb-6 rounded-lg border border-stone/20 bg-cream/30 p-5">
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <Label htmlFor="target-audience">Target audience</Label>
            <Select
              value={data.target_audience || "any"}
              onValueChange={(v) => onChange({ target_audience: v === "any" ? "" : v })}
            >
              <SelectTrigger className="mt-1.5 w-full">
                <SelectValue placeholder="Any audience" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any audience</SelectItem>
                <SelectGroup>
                  <SelectLabel>Residential</SelectLabel>
                  {RESIDENTIAL_AUDIENCES.map((a) => (
                    <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                  ))}
                </SelectGroup>
                <SelectGroup>
                  <SelectLabel>Commercial</SelectLabel>
                  {COMMERCIAL_AUDIENCES.map((a) => (
                    <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="tone">Tone</Label>
            <Select
              value={data.tone}
              onValueChange={(v) => onChange({ tone: v })}
            >
              <SelectTrigger className="mt-1.5 w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TONES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="language">Language</Label>
            <Select
              value={data.language}
              onValueChange={(v) => onChange({ language: v })}
            >
              <SelectTrigger className="mt-1.5 w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((l) => (
                  <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-2">
          <Button
            size="sm"
            onClick={handleGenerate}
            disabled={isGenerating}
            className="bg-red text-white hover:bg-deep-red"
          >
            {activeScript ? "Regenerate script" : "Generate script"}
          </Button>
          {activeScript && !isGenerating && (
            <Badge variant="outline" className="text-[10px]">
              v{activeScript.version}
            </Badge>
          )}
          <button
            type="button"
            onClick={() => setShowNotes((v) => !v)}
            className="ml-auto text-[11px] font-medium text-warm-grey hover:text-charcoal"
          >
            {showNotes ? "\u2212 Hide notes" : "+ Add notes"}
          </button>
        </div>
        {showNotes && (
          <div className="mt-3">
            <Textarea
              id="notes"
              value={data.notes}
              onChange={(e) => onChange({ notes: e.target.value })}
              placeholder="Any specific instructions for the script..."
              rows={2}
              className="text-sm"
            />
          </div>
        )}
      </div>

      {/* Regenerate bar */}
      {activeScript && !isGenerating && showRegenerate && (
        <div className="mb-6 rounded-lg border border-stone/20 bg-cream/30 p-4">
          <div className="flex gap-2">
            <Textarea
              value={regenerateNotes}
              onChange={(e) => setRegenerateNotes(e.target.value)}
              placeholder="Notes for regeneration (optional)..."
              className="h-16 resize-none text-sm"
            />
            <Button
              size="sm"
              onClick={handleGenerate}
              className="shrink-0 bg-red text-white hover:bg-deep-red"
            >
              Go
            </Button>
          </div>
        </div>
      )}

      {/* Failed banner */}
      {scriptFailed && !isGenerating && !activeScript && (
        <div className="mb-6 rounded-lg border border-red/30 bg-red/5 p-4">
          <div className="flex items-start gap-3">
            <svg className="mt-0.5 size-5 shrink-0 text-red" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
            </svg>
            <div>
              <p className="text-sm font-medium text-charcoal">Script generation failed</p>
              <p className="mt-0.5 text-[11px] text-warm-grey">
                Something went wrong. Please try again — if the problem persists, check your listing has enough detail.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Generating progress */}
      {isGenerating && (
        <div className="mb-6 rounded-lg border border-stone/20 bg-cream/30 p-6">
          <div className="flex items-center gap-4">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-stone/20">
              <svg className="size-5 animate-spin text-charcoal" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-charcoal">{generatingMessage || "Writing your script..."}</p>
              <p className="mt-1 text-[11px] text-warm-grey">This usually takes 10–20 seconds.</p>
            </div>
          </div>
        </div>
      )}

      {/* Script content — editable */}
      {!isGenerating && activeScript && content ? (
        <div className="space-y-6">
          {/* Headline */}
          <div className="rounded-lg border border-stone/20 bg-cream/30 p-5">
            <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-warm-grey">
              Headline
            </p>
            <Input
              value={content.headline || ""}
              onChange={(e) => updateContent({ headline: e.target.value })}
              className="border-stone/30 text-lg font-semibold text-charcoal"
            />
          </div>

          {/* Voiceover narration */}
          <div className="rounded-lg border border-stone/20 bg-cream/30 p-5">
            <p className="mb-1 text-[11px] font-medium uppercase tracking-wider text-warm-grey">
              Voiceover narration
            </p>
            <p className="mb-2 text-[11px] text-warm-grey">
              This is the exact text that will be spoken. Edit it to change what the voiceover says.
            </p>
            <Textarea
              value={content.narration || ""}
              onChange={(e) => updateContent({ narration: e.target.value })}
              className="min-h-24 border-stone/30 text-sm text-slate"
              rows={4}
            />
          </div>

          {/* Call to action */}
          <div className="rounded-lg border border-stone/20 bg-cream/30 p-5">
            <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-warm-grey">
              Call to action
            </p>
            <Input
              value={content.cta_text || "Book a viewing"}
              onChange={(e) => updateContent({ cta_text: e.target.value })}
              placeholder="Book a viewing"
              className="border-stone/30 text-sm font-medium text-charcoal"
            />
            <p className="mt-1.5 text-[10px] text-warm-grey">
              Shown on the CTA button in the branding bar.
            </p>
          </div>

          {/* Summary */}
          <div className="rounded-lg border border-stone/20 bg-cream/30 p-5">
            <p className="text-[11px] font-medium uppercase tracking-wider text-warm-grey">
              Summary
            </p>
            <p className="mt-2 text-sm text-slate">
              {contentScenes.length} scene{contentScenes.length !== 1 ? "s" : ""} + closing
              <span className="text-warm-grey"> &middot; ~{Math.round(totalDuration)}s estimated</span>
            </p>
          </div>
        </div>
      ) : !isGenerating ? (
        <div className="flex flex-col items-center rounded-lg border border-stone/20 bg-cream/30 py-12 text-center">
          <div className="mb-3 flex size-12 items-center justify-center rounded-full bg-stone/20">
            <svg className="size-6 text-warm-grey" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
            </svg>
          </div>
          <p className="text-sm text-warm-grey">
            No script yet. Configure your settings above and generate one.
          </p>
        </div>
      ) : null}
    </div>
  )
}
